import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { startCase, update } from 'lodash';
import { addISOWeekYears } from 'date-fns';
import ApplicationBidModal from './ApplicationBidModal';
import { rateTypeSelect } from '../../../utils/forms/preconfiguredInputs';
import KeyValPair from '../../theme/data/KeyValuePair';
import user from '../../../store/reducers/user';
import formatters from '../../../utils/forms/formatters';
import {
	updateBid,
	updateOrCreateApplication,
} from '../../../store/calls/applicationData';
import ConfirmModal from '../../theme/modals/ConfirmModal';
import { toggleGlobalLoader } from '../../../store/actions/session';
import {
	addSuccessAlert,
	addErrorAlert,
} from '../../../store/calls/alertLoadingDispatch';

function ApproveOrRejectBidModal({
	is_approved,
	application_id,
	bidData,
	onCancel,
}) {
	const dispatchApproval = () => {
		toggleGlobalLoader(true);
		updateBid(application_id, bidData, { is_approved })
			.then(() => {
				toggleGlobalLoader(false);
				addSuccessAlert('Bid was updated successfully.');
				onCancel();
			})
			.catch(() => {
				toggleGlobalLoader(false);
				addErrorAlert('There was an error updating the bid.');
			});
	};

	return (
		<ConfirmModal
			visible
			type={is_approved ? 'info' : 'warning'}
			prompt={is_approved ? 'Approve this bid?' : 'Reject this bid?'}
			onAccept={() => {
				dispatchApproval();
				onCancel();
			}}
			onCancel={onCancel}
			data-testid="approve-reject-bid-modal"
		/>
	);
}

function AcceptBidModal({ application_id, bidData, onCancel }) {
	const dispatchAcceptance = () => {
		toggleGlobalLoader(true);
		updateOrCreateApplication({ id: application_id, loan_estimate: bidData.id })
			.then(() => {
				toggleGlobalLoader(false);
				addSuccessAlert('Bid selected.');
				onCancel();
			})
			.catch(() => {
				toggleGlobalLoader(false);
				addErrorAlert('There was an error.');
			});
	};

	return (
		<ConfirmModal
			visible
			type="info"
			prompt="Are you sure you want to accept this bid?"
			onAccept={() => {
				dispatchAcceptance().then(onCancel);
			}}
			onCancel={onCancel}
			data-testid="accept-bid-modal"
		/>
	);
}

const generatePPADisplay = (bidData) => {
	return (
		<div className="">
			<KeyValPair
				val={formatters.percentage.formatter(bidData.annual_riser)}
				_key="Annual Riser"
				striped
			/>
			<KeyValPair
				val={bidData.om_included}
				_key="Operation & Management Expenses Included"
				striped
			/>
			<KeyValPair
				val={bidData.insurance_included}
				_key="Insurance Included"
				striped
			/>
			<KeyValPair val={bidData.buyout_options} _key="Buyout Options" striped />
			<KeyValPair
				val={bidData.conditions}
				_key="Notes and Conditions"
				striped
			/>
		</div>
	);
};

function BidDisplayBox({
	bidData,
	editable,
	selectable,
	showAdmin,
	onEdit,
	onSelect,
	onApprove,
	isApproved,
	isSelected,
	bidBy,
}) {
	const rateTypeDisplay = (rateType) => {
		const { options } = rateTypeSelect;
		const option = options.find((option) => option.value === rateType);
		if (!option) return rateType;
		return option.display;
	};

	let key_val_displayable_keys = Object.keys(bidData).filter(
		(key) =>
			!['rateType', 'id', 'rate', 'term'].includes(key) &&
			bidData[key] !== null &&
			key.indexOf('admin') === -1
	);
	let admin_keys = Object.keys(bidData).filter(
		(key) => key.indexOf('admin') !== -1
	);

	return (
		<div>
			{isSelected && (
				<div className="has-text-primary is-size-3 my-4">Selected Bid</div>
			)}
			<div className="card mt-2 mb-6">
				<div className="card-body">
					<div
						className={`card-header ${
							isSelected ? 'has-background-success' : 'has-background-primary'
						}`}
					>
						<div className="card-header-title has-text-white">
							<div className="is-flex is-align-items-center is-justify-content-space-between is-fullwidth">
								<h3 className="is-size-5 has-text-white mr-6">
									{bidData.rate}
									{rateTypeDisplay(bidData.rate_type)} for {bidData.term} years
								</h3>
								<div className="buttons">
									{editable && (
										<button
											className="button is-primary is-inverted is-outlined mt-1 no-print no-render-print-view" // mt-1 basically just brings it to baseline
											onClick={onEdit}
										>
											Edit
										</button>
									)}
									{showAdmin && [null, undefined].includes(isApproved) && (
										<>
											<button
												className="button is-primary is-inverted is-outlined mt-1 no-print no-render-print-view" // mt-1 basically just brings it to baseline
												onClick={() => onApprove(true)}
											>
												Approve
											</button>
											<button
												className="button is-danger is-inverted is-outlined mt-1 no-print no-render-print-view" // mt-1 basically just brings it to baseline
												onClick={() => onApprove(false)}
											>
												Reject
											</button>
										</>
									)}
									{selectable && isApproved && !isSelected && (
										<button
											className="button is-primary is-inverted is-outlined mt-1 no-print no-render-print-view" // mt-1 basically just brings it to baseline
											onClick={onSelect}
										>
											Select This Bid
										</button>
									)}
								</div>
							</div>
						</div>
					</div>
					<div className="card-content">
						{isApproved === false && showAdmin && (
							<p className="pt-2 pb-5 has-text-danger">
								This bid has been rejected and will not display for the client.
								<a
									href="#"
									className="ml-3 has-text-grey-dark is-underlined"
									onClick={() => onApprove(true)}
								>
									Change to approval
								</a>
							</p>
						)}
						{generatePPADisplay(bidData)}
						{showAdmin && (
							<div className="my-2">
								{admin_keys.map((k) => {
									return (
										<KeyValPair
											key={k}
											val={bidData[k]}
											_key={startCase(k)}
											condensed
										/>
									);
								})}
								<KeyValPair
									data-testid="Bid From"
									val={`${bidBy?.first_name} ${bidBy?.last_name}, ${bidBy?.email}`}
									_key="Bid From"
								/>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

function BidDisplay(props) {
	const application_data = useSelector(
		(state) => state.applicationData?.app || []
	);
	const bids = useSelector((state) => state.applicationData?.bids || []);
	const bid_is_accepted = useSelector((state) =>
		bids
			.map((d) => d.id)
			.includes(state.applicationData?.app?.loan_estimate || -1)
	);
	const requester_permissions = useSelector(
		(state) => state.applicationData?.app?.requester_permissions || []
	);
	console.log(requester_permissions, 'requester_permissions');
	const [bidModalOpen, setBidModalOpen] = useState(false);
	const [editingBid, setEditingBid] = useState(null);
	const [approveRejectModalOpen, setApproveRejectModalOpen] = useState(false);
	const [acceptBidModalOpen, setAcceptBidModalOpen] = useState(false);

	const user_id = useSelector(state => state.user?.decodedTokenData?.user_id || {});

	// useEffect(() => {
	//     console.log("BIDS", bids)
	// }, [])

	return (
		<div className="bid-wrapper wrapper">
			<div className="my-3">
				{requester_permissions.includes('bid') && (
					<div className="level">
						<div className="level-left">
							<div className="level-item">
								<button
									className="button is-primary is-medium"
									onClick={() => setBidModalOpen(true)}
								>
									+ Submit a Bid
								</button>
							</div>
						</div>
					</div>
				)}

				<div className="columns is-multiline">
					<div className="column is-one-half">
						{bids
							.filter((bid) => !bid.is_bid_decline) // Exclude declined bids
							.sort((a, b) =>
								a.id === application_data.loan_estimate ? -1 : 1
							)
							.map((bid, idx) => (
								<BidDisplayBox
									key={bid.id}
									bidData={{ id: bid.id, ...bid.data }}
									editable={
										requester_permissions.includes('edit_requirements') ||
										bid.bid_by.id === user_id
									}
									selectable={
										(requester_permissions.includes('edit') && !bid_is_accepted) ||
										requester_permissions.includes('edit_requirements')
									}
									isSelected={bid.id === application_data.loan_estimate}
									showAdmin={requester_permissions.includes(
										'edit_requirements'
									)}
									bidBy={bid.bid_by}
									onSelect={() => {
										setEditingBid({ id: bid.id, ...bid.data });
										setAcceptBidModalOpen(true);
									}}
									onEdit={() => {
										setEditingBid({ id: bid.id, ...bid.data });
										setBidModalOpen(true);
									}}
									onApprove={(is_approved) => {
										setEditingBid({ id: bid.id, ...bid.data, is_approved });
										setApproveRejectModalOpen(true);
									}}
									isApproved={bid.is_approved}
								/>
							))}
					</div>
				</div>
			</div>

			{bidModalOpen && (
				<ApplicationBidModal
					productType="PPA"
					onCancel={() => {
						setBidModalOpen(false);
						setEditingBid(null);
					}}
					application_id={application_data.id}
					bidData={editingBid}
				/>
			)}

			{approveRejectModalOpen && (
				<ApproveOrRejectBidModal
					is_approved={editingBid.is_approved}
					application_id={application_data.id}
					bidData={editingBid}
					onCancel={() => {
						setApproveRejectModalOpen(false);
						setEditingBid(null);
					}}
				/>
			)}

			{acceptBidModalOpen && (
				<AcceptBidModal
					application_id={application_data.id}
					bidData={editingBid}
					onCancel={() => {
						setAcceptBidModalOpen(false);
						setEditingBid(null);
					}}
				/>
			)}
		</div>
	);
}

export default BidDisplay;
